
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ControlPanel } from '@/components/admin/ControlPanel';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  UserCheck, 
  MessageSquare, 
  Heart,
  Activity,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  FileCheck
} from 'lucide-react';

interface AdminOverviewProps {
  stats: {
    totalUsers: number;
    activeUsers: number;
    pendingVerifications: number;
    totalConnections: number;
    totalMessages: number;
    onlineUsers: number;
  };
  pendingActivities: {
    waiting_verifications: number;
    pending_verifications: number;
    pending_tickets: number;
    total_pending: number;
  };
  loading: boolean;
  loadingActivities: boolean;
}

const AdminOverview = ({ stats, pendingActivities, loading, loadingActivities }: AdminOverviewProps) => {
  const navigate = useNavigate();
  const [statsOpen, setStatsOpen] = useState(true);
  const [controlPanelOpen, setControlPanelOpen] = useState(false);

  const mainStats = [
    {
      title: 'Usuários Totais',
      value: stats.totalUsers,
      icon: Users,
      color: 'text-blue-400'
    },
    {
      title: 'Usuários Ativos',
      value: stats.activeUsers,
      icon: UserCheck,
      color: 'text-green-400'
    },
    {
      title: 'Online Agora',
      value: stats.onlineUsers,
      icon: Activity,
      color: 'text-luxury-red'
    },
    {
      title: 'Conexões Total',
      value: stats.totalConnections,
      icon: Heart,
      color: 'text-pink-400'
    },
    {
      title: 'Mensagens Enviadas',
      value: stats.totalMessages,
      icon: MessageSquare,
      color: 'text-purple-400'
    },
    {
      title: 'Verificações Pendentes',
      value: stats.pendingVerifications,
      icon: Clock,
      color: 'text-yellow-400'
    }
  ];

  if (loading && loadingActivities) {
    return (
      <div className="px-4 pt-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map((i) => (
            <Card key={i} className="bg-white/5 border-white/10 animate-pulse">
              <CardContent className="p-4">
                <div className="h-20 bg-white/10 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pt-8 space-y-6 pb-20">
      {/* Page Title */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Visão Geral do Sistema</h2>
        <p className="text-white/60">Métricas e estatísticas em tempo real da plataforma JOIA</p>
      </div>

      {/* Atividades Pendentes */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="border-b border-white/5">
          <CardTitle className="text-white flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            Atividades Pendentes
          </CardTitle>
          <CardDescription className="text-white/70">
            Itens que requerem atenção imediata
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {loadingActivities ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white/5 rounded-lg p-4 h-24 animate-pulse"></div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Card 1: Verificações Aguardando */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <FileCheck className="h-5 w-5 text-yellow-400" />
                    <span className="text-2xl font-bold text-yellow-400">
                      {pendingActivities.waiting_verifications}
                    </span>
                  </div>
                  <p className="text-white/80 text-sm">Verificações Aguardando</p>
                  <p className="text-white/60 text-xs mt-1">Documentos prontos para revisão</p>
                </div>

                {/* Card 2: Verificações Pendentes */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Clock className="h-5 w-5 text-blue-400" />
                    <span className="text-2xl font-bold text-blue-400">
                      {pendingActivities.pending_verifications}
                    </span>
                  </div>
                  <p className="text-white/80 text-sm">Verificações em Andamento</p>
                  <p className="text-white/60 text-xs mt-1">Aguardando upload de docs</p>
                </div>

                {/* Card 3: Tickets de Suporte */}
                <div className="bg-luxury-red/10 border border-luxury-red/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <MessageSquare className="h-5 w-5 text-luxury-red" />
                    <span className="text-2xl font-bold text-luxury-red">
                      {pendingActivities.pending_tickets}
                    </span>
                  </div>
                  <p className="text-white/80 text-sm">Tickets Abertos</p>
                  <p className="text-white/60 text-xs mt-1">Aguardando resposta</p>
                </div>
              </div>
              
              {/* Botão de ação se houver pendências */}
            {pendingActivities.total_pending > 0 && (
              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={() => navigate('/main/joia/admin/pendencias')}
                  variant="outline" 
                  className="bg-yellow-500/20 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/30"
                >
                  Ver Todas as Pendências
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Estatísticas da Plataforma - Collapsible */}
      <Collapsible open={statsOpen} onOpenChange={setStatsOpen}>
        <Card className="bg-white/5 border-white/10">
          <CollapsibleTrigger className="w-full">
            <CardHeader className="border-b border-white/5">
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Estatísticas da Plataforma
                </span>
                {statsOpen ? (
                  <ChevronDown className="h-5 w-5 text-white/60" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-white/60" />
                )}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mainStats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <Card key={index} className="bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-300">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <p className="text-white/80 text-sm font-medium">{stat.title}</p>
                            <p className="text-2xl font-bold text-white">{stat.value.toLocaleString()}</p>
                          </div>
                          <div className={`p-2 rounded-lg bg-white/10`}>
                            <Icon className={`h-5 w-5 ${stat.color}`} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Aviso de Limite de Listagem */}
      {stats.totalUsers >= 1000 && (
        <Alert className="bg-yellow-500/20 border-yellow-500/30">
          <AlertCircle className="h-4 w-4 text-yellow-400" />
          <AlertTitle className="text-white">Limite de Visualização</AlertTitle>
          <AlertDescription className="text-white/80">
            Existem mais de 1000 usuários cadastrados. As contagens são precisas, mas a lista de usuários na aba "Usuários" mostra apenas os primeiros 1000 registros.
          </AlertDescription>
        </Alert>
      )}

      {/* Painel de Controle - Collapsible (movido para o final) */}
      <Collapsible open={controlPanelOpen} onOpenChange={setControlPanelOpen}>
        <Card className="bg-white/5 border-white/10">
          <CollapsibleTrigger className="w-full">
            <CardHeader className="border-b border-white/5">
              <CardTitle className="text-white flex items-center justify-between">
                <span>Painel de Controle</span>
                {controlPanelOpen ? (
                  <ChevronDown className="h-5 w-5 text-white/60" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-white/60" />
                )}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-6">
              <ControlPanel />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default AdminOverview;
